import { useEffect, useState } from "react"
import { X, MagnifyingGlass } from "@phosphor-icons/react"
import Modal from "../../../components/Modal"
import Loader from "../../../components/Loader"
import {
  useCanonicalNames,
  useUpdatePathologyTermMapping,
} from "../../../hooks/useMissingPathologyLogs"

const NOTES_MAX_LENGTH = 255

// Picker for "Map this term to a canonical pathology". Search is debounced.
const MapPathologyModal = ({ isOpen, onClose, term }) => {
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [selectedCanonicalId, setSelectedCanonicalId] = useState(null)
  const [notes, setNotes] = useState("")

  // Reset local state whenever a different term is being mapped.
  useEffect(() => {
    if (isOpen) {
      setSearch("")
      setDebouncedSearch("")
      setSelectedCanonicalId(null)
      setNotes("")
    }
  }, [isOpen, term?.PathologyTermMappingId])

  // Debounce the search input by ~250ms.
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search), 250)
    return () => clearTimeout(handle)
  }, [search])

  const {
    data: canonicalNames,
    isLoading,
    isError,
    error,
  } = useCanonicalNames({
    BodyPart: term?.BodyPart,
    search: debouncedSearch || undefined,
  })

  const mutation = useUpdatePathologyTermMapping()

  const handleSubmit = () => {
    if (!selectedCanonicalId || !term?.PathologyTermMappingId) return
    mutation.mutate(
      {
        pathologyTermMappingId: term.PathologyTermMappingId,
        Status: "MAPPED",
        PathologyMappingId: selectedCanonicalId,
        Notes: notes,
      },
      {
        onSuccess: () => {
          onClose()
        },
      }
    )
  }

  if (!term) return null

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            Map Pathology Term
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            <span className="italic font-semibold">{term.Pathology}</span> in{" "}
            <span className="font-semibold">{term.BodyPart}</span>
          </p>
        </div>
        <button
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
          type="button"
          onClick={onClose}
        >
          <X size={20} />
        </button>
      </div>

      <div className="space-y-4">
        <div className="relative">
          <MagnifyingGlass
            className="absolute top-1/2 left-3 -translate-y-1/2 text-gray-400"
            size={16}
          />
          <input
            autoFocus
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Search canonical pathology names..."
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="border border-gray-200 rounded-md max-h-72 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader className="border-gray-500" />
            </div>
          ) : isError ? (
            <div className="p-4 text-sm text-red-600">
              {error?.response?.data?.message ||
                error?.message ||
                "Failed to load canonical names"}
            </div>
          ) : !canonicalNames || canonicalNames.length === 0 ? (
            <div className="p-4 text-sm text-gray-500 text-center">
              No canonical names found for this body part.
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {canonicalNames.map((c) => (
                <li
                  key={c.PathologyMappingId}
                  className={`px-3 py-2 text-sm cursor-pointer flex items-center justify-between transition-colors ${
                    selectedCanonicalId === c.PathologyMappingId
                      ? "bg-blue-50 border-l-4 border-l-blue-500 text-blue-800 font-medium"
                      : "hover:bg-gray-50 text-gray-700"
                  }`}
                  onClick={() => setSelectedCanonicalId(c.PathologyMappingId)}
                >
                  <span>{c.CanonicalName}</span>
                  {selectedCanonicalId === c.PathologyMappingId && (
                    <span className="text-xs text-blue-600">Selected</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Notes — optional, max 255 chars. Sent only on MAPPED submissions. */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label
              className="text-xs font-semibold text-gray-700"
              htmlFor="map-pathology-notes"
            >
              Notes
              <span className="ml-1 text-gray-400 font-normal">(optional)</span>
            </label>
            <span
              className={`text-xs ${
                notes.length >= NOTES_MAX_LENGTH
                  ? "text-red-500"
                  : "text-gray-400"
              }`}
            >
              {notes.length}/{NOTES_MAX_LENGTH}
            </span>
          </div>
          <textarea
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-none"
            id="map-pathology-notes"
            maxLength={NOTES_MAX_LENGTH}
            placeholder="Add an optional note about this mapping..."
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>

        <div className="flex gap-3 pt-2">
          <button
            className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            type="button"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!selectedCanonicalId || mutation.isPending}
            type="button"
            onClick={handleSubmit}
          >
            {mutation.isPending ? "Saving..." : "Map"}
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default MapPathologyModal
